import StaticPageShell from "@/components/static_pages/StaticPageShell";

export default function TermsOfUse() {
  return (
    <StaticPageShell title="Terms of Use">
      <div className="mb-8 text-center">
        <h1 className="mb-2 text-3xl font-bold text-gray-900">Terms of Use</h1>
        <p className="text-gray-600">Last updated: July 2025</p>
      </div>

      <div className="prose prose-gray max-w-none">
        <p className="mb-6 text-lg text-gray-700">
          These Terms of Use ("Terms") govern your access to and use of ScoutUX's AI-powered usability testing service
          ("Service") provided by ScoutUX ("we," "our," or "us").
        </p>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">1. Acceptance of Terms</h2>
        <p className="text-gray-700">
          By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any part of
          these Terms, you may not access the Service.
        </p>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">2. Description of Service</h2>
        <p className="mb-4 text-gray-700">
          ScoutUX provides AI-powered usability testing for SaaS applications, including:
        </p>
        <ul className="list-disc space-y-2 pl-6 text-gray-700">
          <li>Automated browser interactions with your websites</li>
          <li>AI analysis based on Nielsen's 10 usability heuristics</li>
          <li>Generation of usability audits with actionable recommendations</li>
          <li>Multi-viewport testing (mobile and desktop)</li>
          <li>User persona management for testing scenarios</li>
        </ul>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">3. User Accounts and Authentication</h2>
        <ul className="list-disc space-y-2 pl-6 text-gray-700">
          <li>You must create an account using Google OAuth to access the Service</li>
          <li>You are responsible for maintaining the security of your account</li>
          <li>You must provide accurate and complete information</li>
          <li>You are responsible for all activities that occur under your account</li>
          <li>You must notify us immediately of any unauthorized use of your account</li>
          <li>
            Creating multiple accounts to circumvent free plan limitations is prohibited and may result in account
            termination
          </li>
        </ul>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">4. Subscription and Payment</h2>
        <h3 className="mb-3 mt-6 text-lg font-semibold text-gray-900">4.1 Subscription Plans</h3>
        <p className="mb-4 text-gray-700">
          We offer various subscription plans with different features and usage limits. Current pricing and plan details
          are available on our website.
        </p>

        <h3 className="mb-3 mt-6 text-lg font-semibold text-gray-900">4.2 Payment Processing</h3>
        <ul className="list-disc space-y-2 pl-6 text-gray-700">
          <li>Payments are processed through Stripe, a third-party payment processor</li>
          <li>You authorize us to charge your payment method for subscription fees</li>
          <li>All fees are due in advance and non-refundable unless otherwise stated</li>
          <li>Subscription fees are billed on a recurring basis according to your selected plan</li>
        </ul>

        <h3 className="mb-3 mt-6 text-lg font-semibold text-gray-900">4.3 Cancellation and Refunds</h3>
        <ul className="list-disc space-y-2 pl-6 text-gray-700">
          <li>
            You may cancel your subscription at any time through your account settings or Stripe's customer portal
          </li>
          <li>Cancellation takes effect at the end of your current billing period</li>
          <li>No refunds are provided for partial subscription periods</li>
          <li>Upon cancellation, you retain access to the Service until the end of your paid period</li>
        </ul>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">5. Acceptable Use</h2>
        <h3 className="mb-3 mt-6 text-lg font-semibold text-gray-900">5.1 Permitted Use</h3>
        <p className="mb-4 text-gray-700">You may use the Service to:</p>
        <ul className="list-disc space-y-2 pl-6 text-gray-700">
          <li>Test websites that you own or have explicit permission to test</li>
          <li>Generate usability audits for legitimate business purposes</li>
          <li>Create and manage user personas for testing scenarios</li>
        </ul>

        <h3 className="mb-3 mt-6 text-lg font-semibold text-gray-900">5.2 Prohibited Use</h3>
        <p className="mb-4 text-gray-700">You may not:</p>
        <ul className="list-disc space-y-2 pl-6 text-gray-700">
          <li>Test websites without proper authorization</li>
          <li>Use the Service for any illegal or unauthorized purpose</li>
          <li>Attempt to disrupt or interfere with the Service</li>
          <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
          <li>Share your account credentials with others</li>
          <li>Exceed usage limits specified in your subscription plan</li>
        </ul>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">6. AI Processing and Data Use</h2>
        <ul className="list-disc space-y-2 pl-6 text-gray-700">
          <li>Our Service uses Google's Gemini AI models to analyze websites and generate audits</li>
          <li>Screenshots and interaction data from your tested websites may be processed by AI services</li>
          <li>AI processing is subject to our Privacy Policy and data processing agreements</li>
          <li>On the free plan, your data may be used by Google to improve their AI models</li>
          <li>On the paid plans, your data is not used to train any AI models</li>
        </ul>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">7. Intellectual Property</h2>
        <h3 className="mb-3 mt-6 text-lg font-semibold text-gray-900">7.1 Our Rights</h3>
        <p className="text-gray-700">
          The Service, including all content, features, and functionality, is owned by ScoutUX and protected by
          copyright, trademark, and other intellectual property laws.
        </p>

        <h3 className="mb-3 mt-6 text-lg font-semibold text-gray-900">7.2 Your Content</h3>
        <ul className="list-disc space-y-2 pl-6 text-gray-700">
          <li>You retain ownership of any content you submit to the Service</li>
          <li>You grant us a limited license to process your content for the purpose of providing the Service</li>
          <li>You are responsible for ensuring you have rights to all content you submit</li>
        </ul>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">8. Service Availability and Modifications</h2>
        <ul className="list-disc space-y-2 pl-6 text-gray-700">
          <li>We strive to maintain high availability but do not guarantee uninterrupted access</li>
          <li>We may modify, suspend, or discontinue the Service at any time</li>
          <li>We may update these Terms from time to time with notice</li>
          <li>Scheduled maintenance will be communicated in advance when possible</li>
        </ul>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">9. Disclaimers and Limitations</h2>
        <h3 className="mb-3 mt-6 text-lg font-semibold text-gray-900">9.1 Service Disclaimer</h3>
        <p className="text-gray-700">
          The Service is provided "as is" without warranties of any kind. We do not guarantee the accuracy,
          completeness, or usefulness of AI-generated usability audits.
        </p>

        <h3 className="mb-3 mt-6 text-lg font-semibold text-gray-900">9.2 Limitation of Liability</h3>
        <p className="text-gray-700">
          To the maximum extent permitted by law, ScoutUX shall not be liable for any indirect, incidental, special,
          consequential, or punitive damages arising from your use of the Service.
        </p>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">10. Data Protection and Privacy</h2>
        <p className="text-gray-700">
          Your privacy is important to us. Our collection and use of personal information is governed by our Privacy
          Policy, which is incorporated into these Terms by reference.
        </p>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">11. Termination</h2>
        <ul className="list-disc space-y-2 pl-6 text-gray-700">
          <li>Either party may terminate your account at any time</li>
          <li>We may terminate your account for violation of these Terms</li>
          <li>Upon termination, your right to use the Service ceases immediately</li>
          <li>You may request deletion of your data by contacting support@scoutux.io</li>
        </ul>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">12. Governing Law</h2>
        <p className="text-gray-700">
          These Terms are governed by and construed in accordance with applicable laws. Any disputes will be resolved
          through binding arbitration or in courts of competent jurisdiction.
        </p>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">13. Contact Information</h2>
        <p className="text-gray-700">If you have questions about these Terms, contact us at:</p>
        <div className="mt-4 rounded-lg bg-gray-50 p-4">
          <p className="font-medium text-gray-700">Email: support@scoutux.io</p>
        </div>

        <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-900">14. Severability</h2>
        <p className="text-gray-700">
          If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full
          force and effect.
        </p>
      </div>
    </StaticPageShell>
  );
}
